import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { Calendar2Outline, FileTextOutline, InfoOutline } from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { RadioGroup, Stack, Typography } from '@lyearn/ui';
import NumberInput from '@/components/NumberInput';
import Radio from '@/components/Radio';
import { ContentEntity, InputMaybe } from '@/types/schema';
import { AssignmentDueDateEnum, DueDateAssignmentConfigInput } from '@/types/schema';

import ContentItem from '../ContentItem';
interface SetDueDateDialogProps {
  content: ContentEntity;
  onDueDateChange: (childResourceId: string, dueDateConfig: DueDateAssignmentConfigInput) => void;
  onClose: () => void;
  dueDateConfig?: InputMaybe<DueDateAssignmentConfigInput>;
  initialDueDateConfig?: InputMaybe<DueDateAssignmentConfigInput>;
}
const Info = ({ t }: { t: TFunction }) => {
  return (
    <Stack
      alignItems="center"
      className="flex flex-col bg-bg-tertiary rounded-8 p-12 mb-20"
      gapY={12}>
      <Stack alignItems="center" className="w-full" gapX={12}>
        <FileTextOutline className="h-18 w-18 text-icon-secondary" fontSize={10} />
        <Typography color="text-text-secondary" variant="body-short-02">
          {t('Path:SetDueDate.InfoL1')}
        </Typography>
      </Stack>
      <Stack alignItems="center" className="flex flex-row w-full" gapX={12}>
        <Calendar2Outline className="h-18 w-18 text-icon-secondary" />
        <Typography color="text-text-secondary" variant="body-short-02">
          {t('Path:SetDueDate.InfoL2')}
        </Typography>
      </Stack>
      <Stack alignItems="center" className="flex flex-row w-full" gapX={12}>
        <InfoOutline className="h-18 w-18 text-icon-secondary" />
        <Typography color="text-text-secondary" variant="body-short-02">
          {t('Path:SetDueDate.InfoL3')}
        </Typography>
      </Stack>
    </Stack>
  );
};
const SetDueDateDialog: React.FC<SetDueDateDialogProps> = ({
  onDueDateChange,
  onClose,
  content,
  dueDateConfig,
  initialDueDateConfig,
}) => {
  const { t } = useTranslation(['Path', 'AssignmentCreator']);
  const [_dueDateConfig, setDueDateConfig] = useState<
    InputMaybe<DueDateAssignmentConfigInput> | undefined
  >(dueDateConfig);
  const DEFAULT_DAYS = 7;

  const resolvedValue = _dueDateConfig?.value ?? DEFAULT_DAYS;

  const onDueDateTypeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, type: string) => {
      const dueDateCofigToUpdate: DueDateAssignmentConfigInput = {
        dueDateType: type as AssignmentDueDateEnum,
        value: null,
      };
      if (type == AssignmentDueDateEnum.Dynamic) {
        dueDateCofigToUpdate.value = resolvedValue;
      }
      setDueDateConfig(dueDateCofigToUpdate);
    },
    [setDueDateConfig, _dueDateConfig],
  );
  const onIntervalChange = useCallback(
    (val: number) => {
      if (_dueDateConfig) {
        setDueDateConfig({
          ..._dueDateConfig,
          value: val,
        });
      }
    },
    [setDueDateConfig, _dueDateConfig],
  );
  const onSubmit = useCallback(() => {
    const { id } = content;
    if (!_dueDateConfig) return;
    onDueDateChange(id, _dueDateConfig);
    onClose();
  }, [setDueDateConfig, _dueDateConfig, content?.id]);

  const showDueDateInfoForEditPath = useMemo(() => {
    return (
      initialDueDateConfig && JSON.stringify(_dueDateConfig) != JSON.stringify(initialDueDateConfig)
    );
  }, [_dueDateConfig, initialDueDateConfig]);
  return (
    <AlertDialog
      open
      contentClassName="max-h-[34rem] mt-20"
      header={{
        title: t('Path:SetDueDate.Heading'),
        description: t('Path:SetDueDate.SubHeading'),
      }}
      primaryAction={{
        label: t('Common:Actions.Save'),
        onClick: onSubmit,
        appearance: 'primary',
      }}
      width={520}
      onClose={onClose}>
      <Stack className="ps-8 pe-8 flex flex-col">
        <Info t={t} />
        <ContentItem className="bg-bg-secondary rounded-8 p-12 mb-20" content={content} />
        <RadioGroup
          aria-label="dueDateType"
          className="mb-32"
          name="dueDateType"
          value={_dueDateConfig?.dueDateType}
          onChange={onDueDateTypeChange}>
          <Radio
            disabled={false}
            label={t('AssignmentCreator:NeverDue')}
            value={AssignmentDueDateEnum.NoDueDate}
          />
          {showDueDateInfoForEditPath &&
          _dueDateConfig?.dueDateType == AssignmentDueDateEnum.NoDueDate ? (
            <Stack alignItems="start" className="ms-30 mt-1">
              <Typography color="text-text-secondary" variant="body-long-02">
                {t('Path:SetDueDate.PathSectionEditInfo')}
              </Typography>
            </Stack>
          ) : null}
          <Radio
            disabled={false}
            label={t('Path:SetDueDate:DueAfterAnIntervalOfDays')}
            value={AssignmentDueDateEnum.Dynamic}
          />
          {_dueDateConfig?.dueDateType === AssignmentDueDateEnum.Dynamic ? (
            <>
              <Stack className="mt-8 ms-26">
                <NumberInput
                  canBeEmpty
                  className="w-256"
                  disabled={false}
                  inputSuffix={t('Common:day', { count: resolvedValue })}
                  max={999}
                  min={0}
                  value={resolvedValue}
                  onChange={onIntervalChange}
                />
              </Stack>
              {showDueDateInfoForEditPath ? (
                <Stack alignItems="start" className="ms-30 mt-8">
                  <Typography color="text-text-secondary" variant="body-long-02">
                    {t('Path:SetDueDate.PathSectionEditInfo')}
                  </Typography>
                </Stack>
              ) : null}
            </>
          ) : null}
        </RadioGroup>
      </Stack>
    </AlertDialog>
  );
};

export default SetDueDateDialog;
